import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import API from '../../API';
import { useAuth } from '../../utils/Auth';
import LoadingSpinner from '../../components/LoadingSpinner';
import TrackerCarousel from '../../components/TrackerComponents/TrackerCarousel';
import { getCustomFieldTypes } from '../../lib/customFieldTypes';
import styles from './PosterPage.module.css';

export default function PosterPage() {
    const { getQRListener } = API;
    const { config } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams();

    const [qrObj, setQrObj] = useState(null);

    const configTypes = useMemo(() => (
        ({
            ...(config?.field_types?.fields || config?.fields || config?.columns || {}),
            ...getCustomFieldTypes(qrObj?.qr_type)
        })
    ), [config, qrObj?.qr_type]);

    const displayFields = useMemo(() => {
        const fields = qrObj?.tracker?.fields || [];
        return fields.filter((field) => {
            if (field?.archived || field?.show === false || field?.type === 'date') return false;
            if (field?.name && typeof field.name === 'object') return true;
            return ['displayText', 'displayImages'].includes(configTypes?.[field?.type]?.category);
        });
    }, [qrObj?.tracker?.fields, configTypes]);

    useEffect(() => {
        let listener;

        if (id) {
            listener = getQRListener(id, setQrObj);
        }

        return () => {
            listener && listener();
            setQrObj(null);
        };
    }, []);

    useEffect(() => {
        if (!qrObj?.qr_type) return;
        if (!['liquid-poster', 'for-sale'].includes(qrObj.qr_type)) {
            navigate('/notfound');
        }
    }, [qrObj, navigate]);

    if (!qrObj || !qrObj?.tracker || !['liquid-poster', 'for-sale'].includes(qrObj?.qr_type)) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.container}>
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <div className={styles.display}>
                    {displayFields.map((field) => (
                        <DisplayField
                            key={field.id}
                            field={field}
                            configType={configTypes?.[field?.type]}
                            trackerFiles={qrObj?.tracker?.files || []}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

const DisplayField = ({ field, configType, trackerFiles }) => {
    if (field?.name && typeof field.name === 'object') {
        const sharedStyles = getSharedSectionStyles(field.styles);
        const headerStyles = {
            fontSize: '2rem',
            fontWeight: '700',
            textAlign: 'center',
            margin: 0,
            ...sharedStyles,
            ...(field.styles?.header || {})
        };
        const paragraphStyles = {
            fontSize: '16px',
            textAlign: 'center',
            margin: 0,
            ...sharedStyles,
            ...(field.styles?.paragraph || {})
        };

        return (
            <div className={styles.headerParagraphBlock}>
                {field.name.header && <h2 style={headerStyles}>{field.name.header}</h2>}
                {field.name.paragraph && <p style={paragraphStyles}>{field.name.paragraph}</p>}
            </div>
        );
    }

    switch (configType?.type || field.type) {
        case 'carousel':
            return (
                <div className={styles.carouselBlock}>
                    <TrackerCarousel files={trackerFiles} />
                </div>
            );
        case 'divider':
            return <hr className={styles.divider} />;
        case 'banner': {
            const bannerColor = field.styles?.color || '#607ca7';
            const bannerBackground = hexToRgba(bannerColor, 0.12);
            const showIcon = field.options?.includes('show-icon');

            return (
                <div
                    className={styles.banner}
                    style={{
                        color: bannerColor,
                        backgroundColor: bannerBackground,
                        borderColor: bannerColor,
                        ...(field.styles || {})
                    }}
                >
                    {showIcon && <i className='far fa-lightbulb-on' />}
                    <span>{field.name || ''}</span>
                </div>
            );
        }
        case 'header':
            return (
                <h2
                    className={styles.header}
                    style={{
                        fontSize: '2rem',
                        fontWeight: '700',
                        textAlign: 'center',
                        ...(field.styles || {})
                    }}
                >
                    {field.name || ''}
                </h2>
            );
        case 'paragraph':
        default:
            return (
                <p
                    className={styles.paragraph}
                    style={{
                        fontSize: '16px',
                        textAlign: 'center',
                        ...(field.styles || {})
                    }}
                >
                    {field.name || ''}
                </p>
            );
    }
};

const getSharedSectionStyles = (stylesState) => {
    if (!stylesState || typeof stylesState !== 'object') return {};
    return Object.fromEntries(
        Object.entries(stylesState).filter(([key]) => !['header', 'paragraph'].includes(key))
    );
};

const hexToRgba = (hex, alpha = 0.12) => {
    let clean = (hex || '').replace('#', '').trim();
    if (clean.length === 3) clean = clean.split('').map((char) => char + char).join('');
    if (clean.length !== 6) return `rgba(96, 124, 167, ${alpha})`;

    const num = parseInt(clean, 16);
    if (Number.isNaN(num)) return `rgba(96, 124, 167, ${alpha})`;

    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
